export type Difficulty = "beginner" | "intermediate" | "advanced";
export type Priority = "must" | "should" | "could";
export type ReqType = "functional" | "non_functional" | "regulatory";
export type RiskLevel = "low" | "medium" | "high";

export interface CaseStudyRequirement {
  title: string;
  raw_text: string;
  req_type: ReqType;
  feature: string;
  channel: string | null;
  contains_phi: boolean;
  market: string | null;
  sensitivity_flags: string[];
  priority: Priority;
  domain: string;
  assumptions: string;
  constraints_list: string;
}

export interface CaseStudyRisk {
  id: string;
  description: string;
  category: string;
  likelihood: RiskLevel;
  impact: RiskLevel;
  mitigation: string;
  owner: string;
}

export interface CaseStudyStakeholder {
  name: string;
  role: string;
  interest: string;
  influence: "high" | "medium" | "low";
}

export interface CaseStudy {
  id: string;
  title: string;
  industry: string;
  phase: string;
  difficulty: Difficulty;
  estimatedHours: number;
  description: string;
  businessProblem: string;
  objectives: string[];
  stakeholders: CaseStudyStakeholder[];
  requirements: CaseStudyRequirement[];
  risks: CaseStudyRisk[];
  successMetrics: string[];
  practiceExercises: string[];
  tags: string[];
}

// ─── Case Studies ──────────────────────────────────────────────────────────────

export const CASE_STUDIES: CaseStudy[] = [
  {
    id: "cs-001",
    title: "Implementing a New CRM System",
    industry: "Healthcare Insurance",
    phase: "Discovery & Requirements",
    difficulty: "beginner",
    estimatedHours: 4,
    description:
      "A mid-size health insurance company wants to replace spreadsheets and legacy tools with a CRM to improve member communication, claims tracking, and agent productivity.",
    businessProblem:
      "Member services teams manage 40,000+ member records across 5 disconnected spreadsheets. Duplicate records, missed follow-ups, and lost communication history are causing member complaints and compliance audit findings. The company needs a single source of truth for member interactions.",
    objectives: [
      "Consolidate member records into a single CRM system",
      "Reduce duplicate member records by 95% within 6 months of go-live",
      "Decrease average handling time per member query from 12 min to 8 min",
      "Achieve 90%+ agent adoption within 3 months",
      "Ensure all member data handling is HIPAA-compliant",
    ],
    stakeholders: [
      { name: "VP Member Services", role: "Executive Sponsor", interest: "Reduce complaint rate and improve NPS score", influence: "high" },
      { name: "Member Services Manager", role: "Business Owner", interest: "Day-to-day agent productivity and SLA tracking", influence: "high" },
      { name: "IT Director", role: "Technical Lead", interest: "Integration with claims and billing systems, security compliance", influence: "high" },
      { name: "Compliance Officer", role: "Reviewer", interest: "HIPAA, data retention, and audit trail requirements", influence: "medium" },
      { name: "Member Services Agents", role: "End Users", interest: "Ease of use, minimal disruption to current workflow", influence: "medium" },
      { name: "CRM Vendor (Salesforce Health Cloud)", role: "Supplier", interest: "Successful implementation and contract renewal", influence: "low" },
    ],
    requirements: [
      {
        title: "Single member profile with full interaction history",
        raw_text: "Agents must be able to view a 360-degree member profile including all calls, emails, claims, and policy changes in one screen without switching systems.",
        req_type: "functional",
        feature: "member profile",
        channel: "portal",
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi", "pii"],
        priority: "must",
        domain: "member management",
        assumptions: "Claims data can be read via existing REST API",
        constraints_list: "Must not store claims data natively — read-only integration only",
      },
      {
        title: "Automated follow-up task creation",
        raw_text: "When a member call ends without resolution, the system shall automatically create a follow-up task assigned to the agent and escalate to a supervisor if unresolved after 48 hours.",
        req_type: "functional",
        feature: "task management",
        channel: null,
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "workflow automation",
        assumptions: "Business hours definition: Mon-Fri 8am-6pm local time",
        constraints_list: "Must support timezone-aware escalation",
      },
      {
        title: "HIPAA audit log for all PHI access",
        raw_text: "Every time an agent views, edits, or exports a member record containing PHI, the system must log: user ID, timestamp, action type, and data fields accessed. Logs must be retained for 6 years.",
        req_type: "regulatory",
        feature: "audit log",
        channel: null,
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi", "audit"],
        priority: "must",
        domain: "compliance",
        assumptions: "Centralized logging service (Splunk) is already available",
        constraints_list: "Retention period: 6 years per HIPAA §164.530(j)",
      },
      {
        title: "Role-based access control",
        raw_text: "The system must restrict PHI visibility based on job role. Agents see only current members in their territory. Supervisors see their team's members. Compliance officers have read-only access to all records.",
        req_type: "regulatory",
        feature: "access control",
        channel: null,
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi"],
        priority: "must",
        domain: "security",
        assumptions: "Active Directory groups will be the source of role assignments",
        constraints_list: "Must pass penetration test before go-live",
      },
      {
        title: "Bulk data migration from legacy spreadsheets",
        raw_text: "All existing member records from 5 source spreadsheets must be migrated into the CRM with deduplication. Duplicates should be merged, not deleted, with a merge audit trail.",
        req_type: "functional",
        feature: "data migration",
        channel: null,
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi", "pii"],
        priority: "must",
        domain: "data management",
        assumptions: "Source data quality assessment will be completed in discovery phase",
        constraints_list: "Zero data loss tolerance — must include reconciliation report",
      },
      {
        title: "Performance: page load under 2 seconds",
        raw_text: "All CRM pages must load in under 2 seconds at peak load (500 concurrent users) to avoid agent productivity loss during open enrollment periods.",
        req_type: "non_functional",
        feature: "performance",
        channel: null,
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "should",
        domain: "performance",
        assumptions: "Peak load modeled on previous open enrollment data",
        constraints_list: "Load testing required pre-launch",
      },
    ],
    risks: [
      { id: "R1", description: "Legacy spreadsheets have inconsistent formats, causing data migration failures or duplicates", category: "Data Quality", likelihood: "high", impact: "high", mitigation: "Run a data quality assessment in discovery phase; build automated deduplication rules; do a pilot migration with 1,000 records before full migration.", owner: "IT Lead" },
      { id: "R2", description: "Agent adoption is low due to change resistance — agents prefer existing spreadsheets", category: "Change Management", likelihood: "medium", impact: "high", mitigation: "Involve agents in UAT; appoint change champions per team; provide hands-on training; run spreadsheets in parallel for the first 30 days.", owner: "Member Services Manager" },
      { id: "R3", description: "CRM vendor fails to meet HIPAA Business Associate Agreement (BAA) requirements", category: "Compliance", likelihood: "low", impact: "high", mitigation: "Require signed BAA before contract; engage legal and compliance in vendor review; review vendor's SOC 2 Type II report.", owner: "Compliance Officer" },
      { id: "R4", description: "Integration with claims system is more complex than estimated, delaying go-live", category: "Technical", likelihood: "medium", impact: "medium", mitigation: "Run a technical spike in discovery to validate API compatibility; add 3-week integration buffer in schedule.", owner: "IT Director" },
      { id: "R5", description: "Cost overrun due to underestimated data cleaning effort", category: "Cost", likelihood: "medium", impact: "medium", mitigation: "Use bottom-up data migration estimate after assessment; add 15% contingency; define scope reduction triggers.", owner: "Project Manager" },
    ],
    successMetrics: [
      "95% reduction in duplicate member records at go-live",
      "Agent average handling time reduced from 12 min to ≤8 min within 90 days",
      "90%+ agent adoption (daily active users) within 3 months",
      "Zero HIPAA audit findings related to CRM data handling in first year",
      "Member satisfaction score (CSAT) improvement of ≥10% within 6 months",
    ],
    practiceExercises: [
      "Draw the 'as-is' member service process and identify 3 pain points",
      "Write 2 additional functional requirements for the member notification feature",
      "Create a RACI matrix for the CRM implementation using the stakeholders listed",
      "Identify which requirements would require a HIPAA Privacy Impact Assessment and explain why",
      "Draft 3 user acceptance test scenarios for the 'single member profile' requirement",
    ],
    tags: ["CRM", "HIPAA", "Data Migration", "Healthcare", "Change Management"],
  },

  {
    id: "cs-002",
    title: "Migrating to Cloud Infrastructure",
    industry: "Healthcare IT Services",
    phase: "Strategy & Planning",
    difficulty: "intermediate",
    estimatedHours: 5,
    description:
      "A healthcare IT services company running patient data platforms on-premise wants to migrate to AWS to reduce costs, improve DR capabilities, and support HIPAA/HITRUST compliance.",
    businessProblem:
      "The company's on-premise data centre lease expires in 18 months. Hardware is 7+ years old, causing 4 outages per year averaging 6 hours each. The DR site is a warm standby that takes 8 hours to activate. Cloud migration would reduce infrastructure costs by an estimated 35% and improve RTO to under 1 hour.",
    objectives: [
      "Migrate all production systems to AWS within 14 months",
      "Reduce infrastructure operating cost by 30% within 12 months post-migration",
      "Achieve RTO of ≤1 hour and RPO of ≤15 minutes for Tier-1 systems",
      "Maintain HIPAA and HITRUST compliance throughout and after migration",
      "Eliminate on-premise data centre dependency before lease expiry",
    ],
    stakeholders: [
      { name: "CTO", role: "Executive Sponsor", interest: "Cost reduction, risk mitigation, competitive positioning", influence: "high" },
      { name: "CISO", role: "Security Approver", interest: "HIPAA/HITRUST compliance, data sovereignty, encryption at rest/transit", influence: "high" },
      { name: "VP Operations", role: "Business Owner", interest: "Zero downtime during migration, SLA preservation", influence: "high" },
      { name: "Infrastructure Team Lead", role: "Technical Lead", interest: "Architecture decisions, team capability development", influence: "medium" },
      { name: "Compliance & Legal", role: "Advisor", interest: "BAA with AWS, data residency, audit evidence", influence: "medium" },
      { name: "Application Teams (5 squads)", role: "Stakeholders", interest: "Minimal disruption to development workflows", influence: "medium" },
    ],
    requirements: [
      {
        title: "PHI data encrypted at rest and in transit",
        raw_text: "All data containing PHI must be encrypted at rest using AES-256 and in transit using TLS 1.2 or higher. Encryption keys must be managed in AWS KMS with quarterly rotation.",
        req_type: "regulatory",
        feature: "data encryption",
        channel: null,
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi", "encryption"],
        priority: "must",
        domain: "security",
        assumptions: "AWS KMS is approved for PHI key management",
        constraints_list: "TLS 1.0 and 1.1 must be disabled; AES-256 minimum",
      },
      {
        title: "Multi-AZ deployment for Tier-1 systems",
        raw_text: "All Tier-1 production systems (patient portal, claims processor, provider API) must be deployed across a minimum of 2 AWS Availability Zones to achieve the required RTO and RPO.",
        req_type: "non_functional",
        feature: "high availability",
        channel: null,
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "infrastructure",
        assumptions: "Tier-1 system list to be confirmed in discovery phase",
        constraints_list: "Must achieve RTO ≤1 hour, RPO ≤15 minutes",
      },
      {
        title: "HIPAA-compliant AWS architecture with signed BAA",
        raw_text: "The AWS architecture must only use HIPAA-eligible services as listed on aws.amazon.com/compliance/hipaa-eligible-services-reference. A Business Associate Agreement must be signed with AWS before any PHI workload is migrated.",
        req_type: "regulatory",
        feature: "compliance",
        channel: null,
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi", "compliance"],
        priority: "must",
        domain: "compliance",
        assumptions: "Legal team will execute BAA in month 1",
        constraints_list: "No non-HIPAA-eligible services may process or store PHI",
      },
      {
        title: "Zero-downtime migration strategy for Tier-1 systems",
        raw_text: "Tier-1 systems must be migrated using a blue-green or canary deployment strategy to avoid service outages. On-premise systems must remain active as fallback until new environment is validated.",
        req_type: "functional",
        feature: "migration strategy",
        channel: null,
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "migration",
        assumptions: "DNS-based traffic switching is feasible for all Tier-1 apps",
        constraints_list: "Rollback plan must be tested before each migration wave",
      },
      {
        title: "Cost tagging and FinOps dashboard",
        raw_text: "All AWS resources must be tagged by system, team, environment, and cost centre. A FinOps dashboard must provide monthly cost allocation reports per business unit within 48 hours of month-end.",
        req_type: "functional",
        feature: "cost management",
        channel: null,
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "should",
        domain: "operations",
        assumptions: "AWS Cost Explorer will be used; Terraform will enforce tagging policy",
        constraints_list: "Tagging policy must cover 100% of resources before first production workload is migrated",
      },
    ],
    risks: [
      { id: "R1", description: "Undetected PHI in non-standard data stores causes HIPAA violation during migration", category: "Compliance", likelihood: "medium", impact: "high", mitigation: "Run a PHI discovery scan (Macie) across all source systems before migration; classify all data before moving.", owner: "CISO" },
      { id: "R2", description: "Migration takes longer than 14 months, exposing company to data centre lease extension costs", category: "Schedule", likelihood: "medium", impact: "high", mitigation: "Build phased migration plan with monthly milestones; prioritize Tier-1 workloads; engage AWS Professional Services.", owner: "CTO" },
      { id: "R3", description: "Network latency to AWS increases application response times above SLA thresholds", category: "Technical", likelihood: "medium", impact: "medium", mitigation: "Conduct network performance testing in AWS before cutting over; use Direct Connect or VPN for latency-sensitive workloads.", owner: "Infrastructure Lead" },
      { id: "R4", description: "Unexpected cloud costs exceed budget due to poor right-sizing", category: "Cost", likelihood: "high", impact: "medium", mitigation: "Use AWS Cost Explorer and right-sizing recommendations; set cost alerts at 80% and 100% of monthly budget.", owner: "Project Manager" },
      { id: "R5", description: "Application dependencies not fully mapped, causing migration sequencing failures", category: "Technical", likelihood: "medium", impact: "high", mitigation: "Perform application dependency mapping using AWS Application Discovery Service before building migration waves.", owner: "Infrastructure Lead" },
    ],
    successMetrics: [
      "100% of Tier-1 systems migrated by month 14",
      "Infrastructure OPEX reduced by ≥30% within 12 months post-migration",
      "RTO ≤1 hour and RPO ≤15 minutes validated by DR test",
      "Zero HIPAA findings related to cloud infrastructure in next audit",
      "Zero Tier-1 outages directly caused by migration activities",
    ],
    practiceExercises: [
      "Create a migration wave plan (6 waves) prioritizing systems by risk and dependency",
      "Write a RACI for the HIPAA compliance workstream of the migration",
      "Identify the 5 most critical non-functional requirements not listed above",
      "Draft an executive status report template for monthly cloud migration updates",
      "Create a test plan outline for the DR failover validation test",
    ],
    tags: ["Cloud", "AWS", "HIPAA", "Infrastructure", "DR", "FinOps"],
  },

  {
    id: "cs-003",
    title: "Employee Self-Service HR Portal",
    industry: "Healthcare Provider",
    phase: "Requirements & Design",
    difficulty: "beginner",
    estimatedHours: 3,
    description:
      "A regional hospital network wants a self-service HR portal so clinical and non-clinical staff can manage leave, view payslips, update personal info, and complete compliance training — reducing HR admin overhead.",
    businessProblem:
      "The HR team of 12 people processes over 800 manual requests per month (leave, payslip queries, personal data changes). 30% of these are simple data lookups that staff could self-serve. HR staff spend 40% of their time on admin rather than strategic HR work. The portal must comply with state employment law and HIPAA for any employee health data.",
    objectives: [
      "Reduce HR manual request volume by 60% within 12 months of go-live",
      "Enable 100% of staff to self-serve leave requests without HR intervention",
      "Provide instant access to payslips, contracts, and compliance certificates",
      "Ensure all employee health data is handled in compliance with HIPAA",
      "Improve employee satisfaction (eNPS) related to HR services by 20 points",
    ],
    stakeholders: [
      { name: "CHRO", role: "Executive Sponsor", interest: "Reduce HR cost-to-serve; improve employee experience", influence: "high" },
      { name: "HR Manager", role: "Business Owner", interest: "Reduce admin workload; accurate leave and payroll data", influence: "high" },
      { name: "IT Manager", role: "Technical Lead", interest: "Integration with HRIS and payroll; SSO; security", influence: "high" },
      { name: "Compliance Officer", role: "Reviewer", interest: "HIPAA for any health data; employment law compliance", influence: "medium" },
      { name: "Clinical Staff (nurses, doctors)", role: "End Users", interest: "Easy mobile access; fast leave approvals", influence: "medium" },
      { name: "Payroll Team", role: "Stakeholder", interest: "Accurate data fed from portal to payroll system", influence: "medium" },
    ],
    requirements: [
      {
        title: "Self-service leave request and approval workflow",
        raw_text: "Staff must be able to submit, track, and cancel leave requests (annual, sick, FMLA) through the portal. Managers must receive email/push notification and approve or reject within the portal. FMLA requests involving medical information must be handled separately with restricted access.",
        req_type: "functional",
        feature: "leave management",
        channel: "portal",
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi", "fmla"],
        priority: "must",
        domain: "HR operations",
        assumptions: "HRIS (Workday) API will provide current leave balances",
        constraints_list: "FMLA medical documentation must not be visible to direct managers — HR only",
      },
      {
        title: "Secure payslip access with 24-month history",
        raw_text: "Employees must be able to view and download their last 24 months of payslips as PDF. Downloads must be watermarked with the employee name and access timestamp.",
        req_type: "functional",
        feature: "payslip access",
        channel: "portal",
        contains_phi: false,
        market: null,
        sensitivity_flags: ["pii"],
        priority: "must",
        domain: "payroll",
        assumptions: "Payroll system (ADP) can generate PDF payslips via API",
        constraints_list: "Payslips must only be accessible to the individual employee, never to managers",
      },
      {
        title: "Personal data self-update with approval workflow",
        raw_text: "Employees can update name, address, bank details, and emergency contact through the portal. Bank detail changes must trigger a dual-approval workflow (HR + Payroll) and a confirmation email to the employee's work address before taking effect.",
        req_type: "functional",
        feature: "personal data management",
        channel: "portal",
        contains_phi: false,
        market: null,
        sensitivity_flags: ["pii", "financial"],
        priority: "must",
        domain: "employee data",
        assumptions: "Dual-approval is required for fraud prevention",
        constraints_list: "Bank detail changes take effect only after both approvals; no same-day processing",
      },
      {
        title: "Mobile-responsive design for clinical staff",
        raw_text: "The portal must be fully usable on mobile devices (iOS and Android) since clinical staff rarely have desktop access during shifts. Core features (leave request, payslip view) must work on 4G with limited connectivity.",
        req_type: "non_functional",
        feature: "mobile access",
        channel: "mobile",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "UX",
        assumptions: "Progressive Web App (PWA) is the agreed approach, not a native app",
        constraints_list: "Must score ≥90 on Google Lighthouse mobile performance",
      },
      {
        title: "Mandatory compliance training tracker",
        raw_text: "The portal must display each employee's outstanding mandatory compliance training (HIPAA, fire safety, infection control) with due dates and allow completion via embedded e-learning modules. Overdue training must trigger automated manager alerts.",
        req_type: "functional",
        feature: "training management",
        channel: "portal",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "should",
        domain: "compliance training",
        assumptions: "LMS (HealthStream) has an API for training record read/write",
        constraints_list: "HIPAA training must be tracked as it is a regulatory requirement",
      },
    ],
    risks: [
      { id: "R1", description: "FMLA medical information is inadvertently exposed to managers due to misconfigured access controls", category: "Compliance", likelihood: "medium", impact: "high", mitigation: "Implement strict role-based access for FMLA documents; test all access scenarios in UAT; get Compliance sign-off before go-live.", owner: "Compliance Officer" },
      { id: "R2", description: "Clinical staff resist adoption due to poor mobile UX or connectivity issues in hospital wards", category: "Change Management", likelihood: "medium", impact: "medium", mitigation: "Involve clinical champions in UX design; pilot in one ward; provide offline capability for core features.", owner: "HR Manager" },
      { id: "R3", description: "Payroll integration errors cause incorrect salary payments after bank detail changes", category: "Operational", likelihood: "low", impact: "high", mitigation: "Implement dual-approval for financial changes; run parallel payroll for first 2 cycles; add reconciliation report.", owner: "Payroll Lead" },
      { id: "R4", description: "Low eNPS improvement if portal is launched without the features staff care most about (leave and payslip)", category: "Benefits", likelihood: "medium", impact: "medium", mitigation: "Ensure leave management and payslip features are in scope for MVP; exclude lower-priority features if timeline is threatened.", owner: "CHRO" },
    ],
    successMetrics: [
      "60% reduction in HR manual requests within 12 months",
      "100% of leave requests submitted via portal within 6 months",
      "Employee portal satisfaction rating ≥4.0/5.0 in first survey",
      "Zero HIPAA incidents related to FMLA data handling",
      "HR admin team able to redirect 40% of time to strategic activities",
    ],
    practiceExercises: [
      "Write a process flow diagram for the FMLA leave request process (as-is vs to-be)",
      "Identify 3 edge cases in the bank detail change approval workflow that could cause errors",
      "Draft acceptance criteria for the mobile leave request feature",
      "Create an impact assessment for clinical staff who work rotating 12-hour shifts",
      "Define the data fields that constitute PHI in this HR context and explain the HIPAA implications",
    ],
    tags: ["HR", "HIPAA", "FMLA", "Self-Service", "Mobile", "Payroll"],
  },

  {
    id: "cs-004",
    title: "Automating the Order-to-Cash Process",
    industry: "Medical Equipment Supplier",
    phase: "Process Analysis & Automation",
    difficulty: "intermediate",
    estimatedHours: 5,
    description:
      "A medical equipment supplier with $120M annual revenue wants to automate its order-to-cash (O2C) process — from purchase order receipt to cash collection — to reduce errors, speed up collections, and free up finance staff.",
    businessProblem:
      "The current O2C process involves 6 manual handoffs between sales, warehouse, billing, and collections. Average days-sales-outstanding (DSO) is 52 days vs industry benchmark of 35 days. 8% of invoices have errors requiring rework. Finance spends 60% of time on manual reconciliation. The company wants to reduce DSO to 38 days and error rate to <2% within 12 months.",
    objectives: [
      "Reduce invoice error rate from 8% to <2% through automated validation",
      "Reduce DSO from 52 days to ≤38 days within 12 months",
      "Automate order confirmation, invoicing, and payment matching",
      "Provide real-time O2C dashboards for finance leadership",
      "Integrate with hospital purchasing systems via EDI/HL7",
    ],
    stakeholders: [
      { name: "CFO", role: "Executive Sponsor", interest: "DSO reduction, working capital improvement, audit risk reduction", influence: "high" },
      { name: "VP Finance", role: "Business Owner", interest: "Process efficiency, staff redeployment, accurate AR aging", influence: "high" },
      { name: "Sales Director", role: "Key Stakeholder", interest: "Order accuracy, no delays in order confirmation to customers", influence: "medium" },
      { name: "IT Manager", role: "Technical Lead", interest: "ERP integration (SAP), EDI connectivity, data security", influence: "high" },
      { name: "Billing Team (8 staff)", role: "End Users", interest: "Less rework, clear exception handling process", influence: "medium" },
      { name: "Hospital Procurement Teams", role: "External Stakeholders", interest: "Accurate invoices, remittance matching, EDI capability", influence: "medium" },
    ],
    requirements: [
      {
        title: "Automated purchase order validation against contract terms",
        raw_text: "The system must automatically validate incoming purchase orders against the customer's contract pricing, approved product catalogue, and credit limit before order confirmation. Orders that fail validation must be flagged for manual review within 2 business hours.",
        req_type: "functional",
        feature: "order validation",
        channel: "edi",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "order management",
        assumptions: "Contract data is available in SAP SD module",
        constraints_list: "Must handle EDI 850 (purchase order) and EDI 855 (order acknowledgement) formats",
      },
      {
        title: "Automated invoice generation and delivery",
        raw_text: "On goods dispatch confirmation, the system must automatically generate and deliver an invoice to the customer within 4 business hours via their preferred channel (EDI 810, email PDF, or customer portal). No manual intervention should be required for standard orders.",
        req_type: "functional",
        feature: "invoicing",
        channel: "edi",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "billing",
        assumptions: "Dispatch confirmation comes from warehouse management system (WMS) API",
        constraints_list: "Must support EDI 810, PDF email, and web portal delivery in MVP",
      },
      {
        title: "Automated payment matching and remittance processing",
        raw_text: "Incoming payments (EFT, check, EDI 820) must be automatically matched to open invoices using invoice number, PO number, or amount. Match rate must be ≥95%. Unmatched payments must be queued for manual review within 1 business day.",
        req_type: "functional",
        feature: "payment matching",
        channel: null,
        contains_phi: false,
        market: null,
        sensitivity_flags: ["financial"],
        priority: "must",
        domain: "collections",
        assumptions: "Bank integration (BAI2 file format) available via existing treasury system",
        constraints_list: "Target auto-match rate: ≥95%; remainder goes to exception queue",
      },
      {
        title: "Real-time O2C dashboard with DSO, aging, and exception KPIs",
        raw_text: "Finance leadership must have access to a real-time dashboard showing: current DSO, AR aging buckets (0-30, 31-60, 61-90, 90+ days), invoice error rate, payment match rate, and top-10 overdue accounts. Dashboard must refresh every 15 minutes.",
        req_type: "functional",
        feature: "reporting",
        channel: "portal",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "should",
        domain: "analytics",
        assumptions: "Power BI is the approved BI tool",
        constraints_list: "Dashboard data latency must not exceed 15 minutes",
      },
      {
        title: "SOX-compliant audit trail for all financial transactions",
        raw_text: "All O2C transactions (order, invoice, payment, credit note, write-off) must have an immutable audit trail including: user/system, timestamp, before/after values, and approval chain. Audit data must be retained for 7 years.",
        req_type: "regulatory",
        feature: "audit trail",
        channel: null,
        contains_phi: false,
        market: null,
        sensitivity_flags: ["financial", "audit"],
        priority: "must",
        domain: "compliance",
        assumptions: "SOX controls apply as the company is a subsidiary of a SEC-registered parent",
        constraints_list: "Audit logs must be read-only and tamper-evident",
      },
    ],
    risks: [
      { id: "R1", description: "EDI connectivity with hospital purchasing systems is technically complex, delaying integration", category: "Technical", likelihood: "high", impact: "high", mitigation: "Conduct EDI capability assessment with top 20 customers in discovery; prioritize highest-volume customers for Phase 1.", owner: "IT Manager" },
      { id: "R2", description: "Automated validation incorrectly rejects valid orders, damaging customer relationships", category: "Operational", likelihood: "medium", impact: "high", mitigation: "Thorough UAT with sales team using real historical orders; build exception handling with fast manual override process.", owner: "Sales Director" },
      { id: "R3", description: "Low auto-match rate for payments causes more exception workload than current manual process", category: "Benefits", likelihood: "medium", impact: "medium", mitigation: "Analyse 6 months of payment data to understand common mismatch patterns before building matching rules.", owner: "VP Finance" },
      { id: "R4", description: "SAP integration scope is larger than estimated due to custom modifications in current system", category: "Technical", likelihood: "high", impact: "medium", mitigation: "Commission SAP technical assessment in discovery phase; identify all custom Z-objects that may affect integration.", owner: "IT Manager" },
    ],
    successMetrics: [
      "Invoice error rate reduced from 8% to <2% within 12 months",
      "DSO reduced from 52 to ≤38 days within 12 months",
      "≥95% of invoices generated automatically without manual intervention",
      "Payment auto-match rate ≥95%",
      "Finance staff admin time reduced by ≥40%",
    ],
    practiceExercises: [
      "Map the current 'as-is' O2C process with all 6 manual handoffs and identify automation opportunities",
      "Write a business case for the DSO improvement using a financial benefit calculation",
      "Define the exception handling process for orders that fail automated validation",
      "Identify the top 3 integration points and write technical requirements for each",
      "Draft 5 UAT test scenarios for the payment auto-matching feature",
    ],
    tags: ["Finance", "Automation", "O2C", "EDI", "SAP", "SOX"],
  },

  {
    id: "cs-005",
    title: "Customer Mobile App for a Health Plan",
    industry: "Health Insurance",
    phase: "Product Requirements",
    difficulty: "intermediate",
    estimatedHours: 5,
    description:
      "A regional health plan wants to build a member-facing mobile app to replace their paper-heavy member experience — enabling digital ID cards, claims status, benefit summaries, provider search, and secure messaging with care managers.",
    businessProblem:
      "70% of member inquiries come through the call centre because members have no digital self-service option. The plan has 180,000 members but 0 digital touchpoints beyond a PDF benefits booklet. App stores show competitor apps rating 4.5+ stars. The board has set a target of 50% digital containment of member inquiries within 24 months.",
    objectives: [
      "Achieve 40% of active members using the app within 12 months of launch",
      "Contain 50% of member inquiries digitally within 24 months, reducing call centre load",
      "Provide members with real-time claims status without calling the plan",
      "Enable digital ID card to be accepted at provider offices and pharmacies",
      "Comply with CMS digital access and interoperability rules (21st Century Cures Act)",
    ],
    stakeholders: [
      { name: "Chief Digital Officer", role: "Executive Sponsor", interest: "Digital transformation KPIs, member NPS improvement", influence: "high" },
      { name: "VP Member Experience", role: "Business Owner", interest: "Call centre deflection, member satisfaction", influence: "high" },
      { name: "CISO", role: "Security Approver", interest: "HIPAA, mobile security, OAuth/FHIR compliance", influence: "high" },
      { name: "Claims Operations Manager", role: "Stakeholder", interest: "Accurate real-time claims status display, avoiding confusion", influence: "medium" },
      { name: "Care Management Director", role: "Stakeholder", interest: "Secure member-to-care-manager messaging capability", influence: "medium" },
      { name: "CMS (Regulator)", role: "External", interest: "Compliance with interoperability and data access rules", influence: "high" },
    ],
    requirements: [
      {
        title: "Digital member ID card with QR code",
        raw_text: "Members must be able to view and present their insurance ID card digitally through the app. The digital ID card must include: member name, member ID, group number, plan name, co-pay amounts, and a QR code scannable by provider office check-in systems. The card must be available offline.",
        req_type: "functional",
        feature: "digital ID card",
        channel: "mobile",
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi", "pii"],
        priority: "must",
        domain: "member services",
        assumptions: "Provider office QR scanner compatibility will be validated with top 20 providers",
        constraints_list: "Must work offline (cached); QR code must refresh every 24 hours for security",
      },
      {
        title: "Real-time claims status with explanation of benefits",
        raw_text: "Members must be able to view the status of all claims submitted in the last 24 months: received, in review, approved, denied, or paid. For closed claims, an explanation of benefits (EOB) summary must be available in plain language, not insurance jargon.",
        req_type: "functional",
        feature: "claims status",
        channel: "mobile",
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi"],
        priority: "must",
        domain: "claims",
        assumptions: "Claims system exposes real-time status via FHIR R4 API",
        constraints_list: "EOB display must meet CMS plain language guidelines; must show member cost responsibility",
      },
      {
        title: "FHIR R4 API compliance for member data access",
        raw_text: "The app must expose member health data (claims, benefits, formulary, provider network) via HL7 FHIR R4 APIs to comply with the CMS Interoperability and Patient Access Final Rule. Members must be able to authorize third-party apps to access their data via OAuth 2.0 SMART on FHIR.",
        req_type: "regulatory",
        feature: "interoperability",
        channel: "api",
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi", "fhir", "cms_compliance"],
        priority: "must",
        domain: "compliance",
        assumptions: "CMS deadline for payer-to-payer data exchange applies to this plan",
        constraints_list: "Must pass CMS FHIR API certification before launch",
      },
      {
        title: "Secure in-app messaging with care managers",
        raw_text: "Members enrolled in care management programs must be able to exchange secure messages with their assigned care manager through the app. Messages containing PHI must be encrypted end-to-end. Care managers must respond within 2 business days. Message history must be retained for 7 years.",
        req_type: "functional",
        feature: "secure messaging",
        channel: "mobile",
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi"],
        priority: "should",
        domain: "care management",
        assumptions: "Care management platform (HealthEdge) has API for message integration",
        constraints_list: "Must use HIPAA-compliant messaging infrastructure (not SMS or standard email)",
      },
      {
        title: "Provider and pharmacy search with real-time network status",
        raw_text: "Members must be able to search for in-network providers and pharmacies by name, specialty, location, and accepted plans. Search results must show current network status (in/out of network) updated weekly from the provider directory. Distance filtering must work within 1 mile accuracy.",
        req_type: "functional",
        feature: "provider search",
        channel: "mobile",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "member services",
        assumptions: "Provider directory API is available; updates weekly",
        constraints_list: "Network status accuracy is critical — incorrect OON display creates member liability risk",
      },
    ],
    risks: [
      { id: "R1", description: "FHIR API development is more complex than estimated, delaying CMS compliance deadline", category: "Compliance", likelihood: "medium", impact: "high", mitigation: "Engage a FHIR specialist in discovery; use an established FHIR gateway product rather than building from scratch.", owner: "CISO" },
      { id: "R2", description: "Members don't trust the app with their health data, leading to low adoption", category: "Change Management", likelihood: "medium", impact: "high", mitigation: "Invest in transparent privacy communications; show biometric login and encryption prominently; run member focus groups before launch.", owner: "VP Member Experience" },
      { id: "R3", description: "Claims data shown in the app is incorrect or delayed, causing member calls to increase", category: "Operational", likelihood: "medium", impact: "high", mitigation: "Define data freshness SLA with claims team; build status page showing last data refresh time; add clear disclaimers.", owner: "Claims Operations Manager" },
      { id: "R4", description: "Digital ID card not accepted at provider offices because QR scanner not installed", category: "Benefits", likelihood: "high", impact: "medium", mitigation: "Negotiate with top 50 providers to accept digital ID; maintain paper card option for first 12 months.", owner: "VP Member Experience" },
    ],
    successMetrics: [
      "40% of active members using the app monthly within 12 months",
      "50% digital containment of member inquiries within 24 months",
      "App store rating ≥4.3 stars within 6 months of launch",
      "Zero CMS interoperability compliance findings",
      "Call centre volume related to claims status reduced by ≥30% within 12 months",
    ],
    practiceExercises: [
      "Create a member journey map for a member checking their claim status (from incident to resolution)",
      "Write plain-language EOB content for a denied mental health claim",
      "Identify all PHI fields displayed in the app and classify their sensitivity",
      "Draft a privacy impact assessment scope for the secure messaging feature",
      "Write acceptance criteria for the provider search feature including edge cases (no results, OON provider)",
    ],
    tags: ["Mobile App", "HIPAA", "FHIR", "CMS", "Health Insurance", "Member Experience"],
  },

  {
    id: "cs-006",
    title: "Enterprise Data Warehouse & BI Solution",
    industry: "Integrated Health System",
    phase: "Data Strategy",
    difficulty: "advanced",
    estimatedHours: 6,
    description:
      "A 12-hospital integrated health system wants a centralized data warehouse and self-service BI platform to unify clinical, financial, and operational data and enable data-driven decisions at the executive and department level.",
    businessProblem:
      "Data lives in 14 disconnected systems (Epic, SAP, Kronos, custom apps). Executives receive weekly Excel reports that are outdated by the time they're read. Analysts spend 70% of time extracting and reconciling data rather than analysing it. There is no single source of truth for key metrics such as length-of-stay, cost-per-case, or staff-to-patient ratios.",
    objectives: [
      "Create a single source of truth for clinical, financial, and workforce data",
      "Enable self-service analytics for 200+ business users without IT dependency",
      "Reduce analyst report-preparation time by 60%",
      "Deliver C-suite dashboards with ≤24-hour data latency",
      "Comply with HIPAA de-identification standards for analytics use cases",
    ],
    stakeholders: [
      { name: "CEO / Board", role: "Executive Sponsor", interest: "Strategic KPIs: quality, financial performance, growth", influence: "high" },
      { name: "CDO (Chief Data Officer)", role: "Business Owner", interest: "Data governance, single source of truth, data literacy", influence: "high" },
      { name: "CFO", role: "Key Stakeholder", interest: "Cost-per-case, labor cost, budget variance", influence: "high" },
      { name: "CMO", role: "Key Stakeholder", interest: "Length-of-stay, readmission rates, quality metrics", influence: "high" },
      { name: "CISO", role: "Security Approver", interest: "HIPAA de-identification, access controls, data lineage", influence: "high" },
      { name: "Analytics Team (15 analysts)", role: "End Users", interest: "Self-service tools, reliable data, minimal ETL work", influence: "medium" },
    ],
    requirements: [
      {
        title: "Unified data model covering clinical, financial, and workforce domains",
        raw_text: "The data warehouse must include a unified data model with conformed dimensions (patient, provider, facility, date, payer) and domain-specific fact tables for: clinical events, financial transactions, and workforce scheduling. Model must follow industry standards (HL7 FHIR for clinical, UFAP for financial).",
        req_type: "functional",
        feature: "data model",
        channel: null,
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi"],
        priority: "must",
        domain: "data architecture",
        assumptions: "Epic Clarity database will be primary clinical data source",
        constraints_list: "Must support both de-identified (analytics) and identified (operational) data tiers",
      },
      {
        title: "HIPAA Safe Harbor de-identification for analytics tier",
        raw_text: "The analytics tier of the data warehouse must de-identify all patient data using HIPAA Safe Harbor (18 identifiers removed) or Expert Determination method. De-identified datasets must be available for self-service analytics. Access to identified data must be restricted to approved clinical operations use cases.",
        req_type: "regulatory",
        feature: "de-identification",
        channel: null,
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi", "de-identification"],
        priority: "must",
        domain: "compliance",
        assumptions: "Expert determination method requires a qualified statistician sign-off",
        constraints_list: "Re-identification risk assessment must be performed annually",
      },
      {
        title: "Self-service BI with row-level security",
        raw_text: "Business users must be able to build their own reports and dashboards in Power BI without IT assistance. Row-level security must restrict data access by facility, department, and role. A business user in Hospital 3 must not be able to see Hospital 7 data.",
        req_type: "functional",
        feature: "self-service BI",
        channel: "portal",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "analytics",
        assumptions: "Power BI Premium is the approved BI platform",
        constraints_list: "Row-level security rules must be maintained in Active Directory groups, not Power BI manually",
      },
      {
        title: "Executive dashboard with KPI alerts",
        raw_text: "C-suite executives must have a single-page dashboard showing: EBITDA margin, cost-per-case, average LOS, readmission rate, occupancy rate, and staff-to-patient ratio. Metrics that breach thresholds must trigger an email alert within 1 hour of the breach.",
        req_type: "functional",
        feature: "executive dashboard",
        channel: "portal",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "reporting",
        assumptions: "KPI thresholds will be defined and owned by the CDO",
        constraints_list: "Dashboard data must be no older than 24 hours; alert latency ≤1 hour",
      },
    ],
    risks: [
      { id: "R1", description: "Epic Clarity extraction breaks due to Epic version upgrades, causing data pipeline failures", category: "Technical", likelihood: "high", impact: "high", mitigation: "Version-lock extraction queries to Epic Clarity schema version; build automated schema-change detection; maintain Epic DBA involvement.", owner: "CDO" },
      { id: "R2", description: "Business users distrust data quality, leading to shadow spreadsheets continuing", category: "Benefits", likelihood: "high", impact: "high", mitigation: "Publish data quality scores per domain; run workshops with finance and clinical teams to validate key metrics before launch.", owner: "CDO" },
      { id: "R3", description: "PHI accidentally exposed in the de-identified analytics tier due to suppression errors", category: "Compliance", likelihood: "medium", impact: "high", mitigation: "Automated de-identification testing with synthetic patient data; third-party HIPAA privacy audit before go-live.", owner: "CISO" },
      { id: "R4", description: "Scope expands to include all 14 source systems simultaneously, making delivery unachievable", category: "Scope", likelihood: "high", impact: "high", mitigation: "Phase delivery: Epic + SAP in Phase 1 (covers 80% of key KPIs); add remaining systems in Phases 2–3.", owner: "Project Manager" },
    ],
    successMetrics: [
      "200 business users self-serving analytics within 12 months",
      "Analyst report prep time reduced by ≥60%",
      "C-suite dashboards updated within 24 hours of data refresh",
      "Zero HIPAA incidents related to de-identified data exposure",
      "Business user satisfaction with data quality ≥4.0/5.0",
    ],
    practiceExercises: [
      "Define the 5 conformed dimensions for a healthcare data warehouse and explain why they are 'conformed'",
      "Identify the 18 HIPAA Safe Harbor identifiers and explain which are most risky in a hospital dataset",
      "Write a data governance charter for the analytics team covering data ownership, quality, and access",
      "Design the row-level security model for a 3-hospital system with shared services",
      "Create a phasing plan for the 14-source-system migration and justify your prioritization",
    ],
    tags: ["Data Warehouse", "BI", "HIPAA", "Power BI", "Epic", "Analytics"],
  },

  {
    id: "cs-007",
    title: "Customer Support Ticketing System",
    industry: "Health Insurance",
    phase: "Process Improvement",
    difficulty: "beginner",
    estimatedHours: 3,
    description:
      "A health plan's member services department wants to replace shared email inboxes with a proper ticketing system to improve response times, SLA tracking, and member satisfaction.",
    businessProblem:
      "The member services team uses 3 shared Outlook mailboxes for member queries. There is no tracking, no SLA visibility, and no way to see which queries are overdue or unassigned. First response time averages 3 days. Regulatory complaints have cited the plan for not responding to member grievances within CMS-required timeframes.",
    objectives: [
      "Achieve CMS-required grievance response within 30 days for 100% of cases",
      "Reduce first response time from 3 days to ≤4 business hours",
      "Provide real-time SLA tracking for supervisors",
      "Automatically route tickets by query type to the right team",
      "Create a complete audit trail for regulatory examination",
    ],
    stakeholders: [
      { name: "VP Member Services", role: "Executive Sponsor", interest: "Regulatory compliance, NPS improvement, cost reduction", influence: "high" },
      { name: "Member Services Manager", role: "Business Owner", interest: "SLA visibility, agent workload management, escalation tracking", influence: "high" },
      { name: "Compliance Officer", role: "Reviewer", interest: "Grievance tracking, audit trail, CMS reporting", influence: "high" },
      { name: "IT Manager", role: "Technical Lead", interest: "SSO, integration with CRM, email routing configuration", influence: "medium" },
      { name: "Member Services Agents (25)", role: "End Users", interest: "Clear queue, easy ticket management, no extra steps", influence: "medium" },
    ],
    requirements: [
      {
        title: "Automatic ticket creation from member email and web form",
        raw_text: "All member emails to the support address and submissions via the member web form must automatically create a ticket in the system within 2 minutes. The ticket must be categorized (claim query, billing, grievance, general) and assigned to the appropriate team queue based on keyword detection.",
        req_type: "functional",
        feature: "ticket creation",
        channel: "email",
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi"],
        priority: "must",
        domain: "ticket management",
        assumptions: "Email routing rules will be configured to forward to ticketing system inbox",
        constraints_list: "Must handle 500+ inbound emails per day at peak",
      },
      {
        title: "SLA tracking with automated escalation",
        raw_text: "The system must track elapsed time from ticket creation for each SLA category: general queries (4 business hours first response, 2 business days resolution), grievances (24 hours acknowledgement, 30 calendar days resolution). Tickets approaching SLA breach must alert supervisors at 75% of SLA time elapsed.",
        req_type: "functional",
        feature: "SLA management",
        channel: null,
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "service management",
        assumptions: "Business hours: Mon–Fri 8am–6pm EST",
        constraints_list: "Grievance SLA must follow CMS 42 CFR 422.564 requirements exactly",
      },
      {
        title: "Grievance and appeal tracking with CMS reporting",
        raw_text: "The system must separately track all member grievances and appeals with: receipt date, acknowledgement date, resolution date, resolution type, and CMS-required outcome categories. Monthly and annual reports must be exportable in CMS-required format.",
        req_type: "regulatory",
        feature: "grievance management",
        channel: null,
        contains_phi: true,
        market: null,
        sensitivity_flags: ["phi", "cms_compliance"],
        priority: "must",
        domain: "compliance",
        assumptions: "CMS reporting requirements defined in 42 CFR 422.564 and 422.562",
        constraints_list: "Grievance audit trail must be retained for 10 years per CMS requirements",
      },
      {
        title: "Agent productivity and queue dashboard",
        raw_text: "Supervisors must have a real-time dashboard showing: open tickets by queue, tickets by SLA status (on track, at risk, breached), agent workload (open tickets per agent), and first-response-time trend over 30 days.",
        req_type: "functional",
        feature: "supervisor dashboard",
        channel: "portal",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "should",
        domain: "reporting",
        assumptions: "Dashboard updates in real-time (no more than 5-minute latency)",
        constraints_list: "Must be accessible on tablet for supervisors on the floor",
      },
    ],
    risks: [
      { id: "R1", description: "Grievance tickets miscategorized as general queries, causing CMS SLA breaches", category: "Compliance", likelihood: "medium", impact: "high", mitigation: "Build specific grievance keyword detection rules; require human review for any ticket using grievance-related keywords; audit monthly.", owner: "Compliance Officer" },
      { id: "R2", description: "Agent adoption fails because new system is more complex than shared email", category: "Change Management", likelihood: "medium", impact: "medium", mitigation: "Involve agents in tool selection; keep UI simple; provide 2-hour hands-on training per agent; run shared email in parallel for 30 days.", owner: "Member Services Manager" },
      { id: "R3", description: "Email routing configuration loses some member emails during transition", category: "Operational", likelihood: "low", impact: "high", mitigation: "Dual-route emails to both old inbox and new system for 2 weeks post-cutover; monitor for missing tickets daily.", owner: "IT Manager" },
    ],
    successMetrics: [
      "100% of grievances acknowledged within 24 hours within 3 months of go-live",
      "First response time reduced from 3 days to ≤4 business hours within 6 months",
      "Zero CMS SLA compliance breaches within 12 months of go-live",
      "Agent satisfaction with ticketing tool ≥3.5/5.0",
      "Supervisor visibility: 100% of near-breach tickets visible ≥2 hours before SLA deadline",
    ],
    practiceExercises: [
      "Map the current email-based query process and identify all failure points",
      "Write the business rules for automatic ticket categorization (at least 5 categories with keywords)",
      "Draft the CMS grievance SLA requirement in formal requirement language (as a MUST statement)",
      "Create acceptance criteria for the SLA escalation alert feature",
      "Design a test scenario where a grievance is initially miscategorized and trace the risk to CMS compliance",
    ],
    tags: ["CRM", "Ticketing", "CMS Compliance", "SLA", "Grievance", "Member Services"],
  },

  {
    id: "cs-008",
    title: "Online Training and Onboarding Platform",
    industry: "Healthcare Provider",
    phase: "Requirements & Vendor Selection",
    difficulty: "beginner",
    estimatedHours: 3,
    description:
      "A hospital network's Learning & Development team wants a digital LMS to replace paper-based onboarding so new clinical hires can complete mandatory training faster and managers can track compliance in real time.",
    businessProblem:
      "New hires currently complete 40+ hours of mandatory training via in-person sessions that are scheduled monthly. Time-to-productivity is 8 weeks. 15% of mandatory compliance training (HIPAA, fire safety, infection control) is overdue at any given time, creating regulatory risk. There is no system of record — completion is tracked in an Excel file by 2 HR coordinators.",
    objectives: [
      "Reduce time-to-productivity for new clinical hires from 8 weeks to 5 weeks",
      "Achieve 100% mandatory training compliance at all times (0 overdue)",
      "Enable managers to track team compliance without contacting HR",
      "Support 200 new hires per month at peak onboarding periods",
      "Comply with HIPAA, Joint Commission, and state health department training requirements",
    ],
    stakeholders: [
      { name: "Chief Nursing Officer", role: "Executive Sponsor", interest: "Reduce time-to-productive-care; maintain Joint Commission compliance", influence: "high" },
      { name: "L&D Director", role: "Business Owner", interest: "Platform ease-of-use, content authoring, completion reporting", influence: "high" },
      { name: "HR Director", role: "Key Stakeholder", interest: "Onboarding workflow integration, new hire experience", influence: "medium" },
      { name: "Compliance Officer", role: "Reviewer", interest: "HIPAA training records, audit-ready reporting, retention", influence: "high" },
      { name: "Nurse Managers (40)", role: "End Users", interest: "Team compliance dashboard, minimal admin work", influence: "medium" },
      { name: "New Clinical Hires", role: "Learners", interest: "Mobile access, relevant content, no repeated training", influence: "medium" },
    ],
    requirements: [
      {
        title: "Mandatory training curriculum management with due date automation",
        raw_text: "The system must automatically assign the correct mandatory training curriculum to each new hire based on their job role (RN, CNA, physician, admin) on their start date. Due dates must be automatically calculated per regulatory requirement (e.g., HIPAA training due within 30 days of hire, annual recertification 12 months from completion).",
        req_type: "functional",
        feature: "curriculum management",
        channel: "portal",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "learning management",
        assumptions: "Job role data will be fed from HRIS (Workday) on hire date",
        constraints_list: "Must support at least 15 distinct role-based curricula in MVP",
      },
      {
        title: "Mobile-accessible learning modules for ward use",
        raw_text: "All e-learning modules must be accessible on iOS and Android mobile devices so clinical staff can complete training during break periods on the ward. Modules must support offline completion with sync when reconnected to the network.",
        req_type: "non_functional",
        feature: "mobile learning",
        channel: "mobile",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "UX",
        assumptions: "xAPI (Tin Can) standard will be used for offline SCORM tracking",
        constraints_list: "Offline mode must support up to 48 hours without network connectivity",
      },
      {
        title: "Compliance training audit trail with 6-year retention",
        raw_text: "The system must maintain an immutable record of all training completions including: employee ID, training title, completion date, score, attempt count, and the version of the content completed. Records must be retained for 6 years to support HIPAA and Joint Commission audits.",
        req_type: "regulatory",
        feature: "audit trail",
        channel: null,
        contains_phi: false,
        market: null,
        sensitivity_flags: ["audit"],
        priority: "must",
        domain: "compliance",
        assumptions: "Joint Commission survey occurs every 3 years; HIPAA audit can cover 6-year lookback",
        constraints_list: "Completion records must be exportable in CSV and PDF format for audit submissions",
      },
      {
        title: "Manager compliance dashboard with overdue alerts",
        raw_text: "Nurse managers and department heads must have a real-time dashboard showing each team member's training completion status by curriculum. Overdue training items must be highlighted in red and trigger an automated weekly email to the manager until resolved.",
        req_type: "functional",
        feature: "manager dashboard",
        channel: "portal",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "reporting",
        assumptions: "Manager-to-employee relationships come from HRIS",
        constraints_list: "Dashboard must cover direct reports only, not skip-level reports, by default",
      },
    ],
    risks: [
      { id: "R1", description: "Low learner engagement with e-learning modules causes completion rates to fall below compliance threshold", category: "Benefits", likelihood: "medium", impact: "high", mitigation: "Use microlearning format (modules ≤15 min); include scenario-based learning; gamify completion with badges; make mobile-first.", owner: "L&D Director" },
      { id: "R2", description: "HRIS integration fails to automatically assign curricula, requiring manual assignment and creating gaps", category: "Technical", likelihood: "medium", impact: "high", mitigation: "Build fallback manual assignment as backstop; reconcile HRIS vs LMS assignments weekly for first 3 months.", owner: "HR Director" },
      { id: "R3", description: "Content migration from existing modules (SCORM 1.2) fails in new LMS", category: "Technical", likelihood: "medium", impact: "medium", mitigation: "Conduct content audit and compatibility testing in discovery phase; budget for re-authoring incompatible modules.", owner: "L&D Director" },
    ],
    successMetrics: [
      "Time-to-productivity for new clinical hires reduced from 8 to ≤5 weeks",
      "Mandatory training overdue rate reduced from 15% to <1% within 12 months",
      "Manager dashboard adoption: ≥80% of managers checking weekly within 3 months",
      "Learner satisfaction with platform ≥4.0/5.0",
      "Zero Joint Commission findings related to training record gaps",
    ],
    practiceExercises: [
      "Create a role-based curriculum matrix for 5 clinical roles listing mandatory training and due dates",
      "Write a requirements traceability matrix linking each training requirement to a specific regulation",
      "Design the 'new hire onboarding' workflow from offer acceptance to first day and identify LMS touch points",
      "Identify 3 risks not listed above that are specific to a multi-site hospital network",
      "Draft an RFP evaluation scorecard for LMS vendor selection with weighted criteria",
    ],
    tags: ["LMS", "Training", "Onboarding", "HIPAA", "Joint Commission", "Compliance"],
  },

  {
    id: "cs-009",
    title: "Procurement Process Redesign",
    industry: "Healthcare Provider Network",
    phase: "Business Process Re-engineering",
    difficulty: "intermediate",
    estimatedHours: 5,
    description:
      "A 6-hospital network's procurement process takes 45 days on average for non-emergency purchases, causing supply chain delays. The team wants to redesign the process and implement a digital approval workflow to reduce cycle time to ≤10 days.",
    businessProblem:
      "The current procurement process involves paper-based purchase requisitions, physical approvals from 3 managers, and manual data entry into the ERP. 30% of purchase orders have errors requiring rework. Preferred vendors are not being used consistently, losing volume discounts worth an estimated $2.1M annually. The process has 12 manual steps and no visibility into status.",
    objectives: [
      "Reduce procurement cycle time from 45 days to ≤10 days for standard purchases",
      "Increase preferred vendor utilization from 60% to 85%",
      "Eliminate paper-based requisition and approval process",
      "Reduce PO error rate from 30% to <5%",
      "Capture the estimated $2.1M in lost volume discounts within 12 months",
    ],
    stakeholders: [
      { name: "CFO", role: "Executive Sponsor", interest: "Cost savings, working capital, audit readiness", influence: "high" },
      { name: "VP Supply Chain", role: "Business Owner", interest: "Process efficiency, vendor relationships, compliance", influence: "high" },
      { name: "Department Heads (clinical and non-clinical)", role: "End Users", interest: "Fast approvals, visibility into request status", influence: "medium" },
      { name: "Accounts Payable Manager", role: "Stakeholder", interest: "Accurate invoices, 3-way match, payment terms compliance", influence: "medium" },
      { name: "IT Manager", role: "Technical Lead", interest: "ERP integration (Oracle), SSO, workflow configuration", influence: "medium" },
      { name: "Preferred Vendors (top 20)", role: "External", interest: "Accurate POs, timely payment, digital catalogue integration", influence: "low" },
    ],
    requirements: [
      {
        title: "Digital purchase requisition with smart form validation",
        raw_text: "Requestors must submit purchase requisitions via a digital form that validates: vendor is on the approved list, price is within contracted range, cost code is valid, and the requisition does not exceed the requestor's approval authority. Invalid requests must be rejected with a specific error message before submission.",
        req_type: "functional",
        feature: "purchase requisition",
        channel: "portal",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "procurement",
        assumptions: "Approved vendor catalogue and contract pricing available in Oracle",
        constraints_list: "Preferred vendor substitution suggestion must appear when non-preferred vendor is selected",
      },
      {
        title: "Automated approval routing based on value and category",
        raw_text: "Approval routing must be automatically determined based on: purchase value (≤$5k: 1 approver, $5k–$50k: 2 approvers, >$50k: 3 approvers + CFO), purchase category (clinical supplies require clinical director approval), and budget availability. Approvals must be available via email one-click and mobile app.",
        req_type: "functional",
        feature: "approval workflow",
        channel: "email",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "workflow",
        assumptions: "Budget availability checked against Oracle budget module in real-time",
        constraints_list: "Approver escalation after 48 hours of no response to their manager",
      },
      {
        title: "Three-way match automation (PO, receipt, invoice)",
        raw_text: "When a supplier invoice is received, the system must automatically match it against the PO and goods receipt note. Invoices that match within 2% tolerance must be automatically approved for payment. Exceptions (>2% variance, missing receipt) must be routed to AP for manual review within 1 business day.",
        req_type: "functional",
        feature: "invoice matching",
        channel: null,
        contains_phi: false,
        market: null,
        sensitivity_flags: ["financial"],
        priority: "must",
        domain: "accounts payable",
        assumptions: "Supplier invoices received via EDI 810 or email-to-PDF extraction (OCR)",
        constraints_list: "Auto-approval tolerance: ±2% or ±$50, whichever is lower",
      },
      {
        title: "Spend analytics dashboard with preferred vendor tracking",
        raw_text: "The VP Supply Chain and CFO must have a dashboard showing: spend by category and vendor, preferred vs non-preferred vendor split, savings achieved vs opportunity, top 20 vendor spend trend, and budget utilization by department. Data must be updated daily.",
        req_type: "functional",
        feature: "spend analytics",
        channel: "portal",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "should",
        domain: "analytics",
        assumptions: "Power BI connected to Oracle Procurement module",
        constraints_list: "Preferred vendor split must be drillable to individual PO level for vendor negotiations",
      },
    ],
    risks: [
      { id: "R1", description: "Department heads bypass new system for urgent purchases, creating shadow procurement", category: "Operational", likelihood: "high", impact: "high", mitigation: "Executive mandate that all non-emergency purchases go through the system; post-launch audit of POs created outside the tool; fast-track emergency approval path in the system.", owner: "VP Supply Chain" },
      { id: "R2", description: "Preferred vendor catalogue is incomplete or inaccurate, leading to unnecessary non-preferred purchases", category: "Data Quality", likelihood: "medium", impact: "high", mitigation: "Vendor catalogue cleansing project to run in parallel with system implementation; ongoing catalogue governance process.", owner: "VP Supply Chain" },
      { id: "R3", description: "3-way match automation has low match rate due to poor invoice data from suppliers", category: "Technical", likelihood: "medium", impact: "medium", mitigation: "Run OCR accuracy testing with top 50 suppliers before go-live; mandate EDI 810 for suppliers >$100k annual spend.", owner: "AP Manager" },
      { id: "R4", description: "Oracle integration is more complex than estimated due to heavy customization of existing instance", category: "Technical", likelihood: "high", impact: "medium", mitigation: "Engage Oracle consultant to assess integration complexity in discovery; add 20% schedule contingency for integration work.", owner: "IT Manager" },
    ],
    successMetrics: [
      "Procurement cycle time reduced from 45 to ≤10 days within 12 months",
      "Preferred vendor utilization increased from 60% to ≥85% within 12 months",
      "PO error rate reduced from 30% to <5% within 6 months",
      "≥$1.5M in volume discount savings captured in first 12 months",
      "3-way match auto-approval rate ≥90%",
    ],
    practiceExercises: [
      "Draw the current 12-step procurement process and eliminate manual steps in your redesign",
      "Write the business rules for the automated approval routing logic as a decision table",
      "Identify all integration points between the procurement tool and Oracle and write requirements for each",
      "Calculate the ROI of the procurement project using the provided data points",
      "Create a change management plan for department heads who currently bypass the old process",
    ],
    tags: ["Procurement", "ERP", "Oracle", "Workflow Automation", "Spend Analytics", "AP"],
  },

  {
    id: "cs-010",
    title: "Project Portfolio Management (PPM) Tool",
    industry: "Healthcare IT Services",
    phase: "Portfolio Governance",
    difficulty: "advanced",
    estimatedHours: 6,
    description:
      "A healthcare IT services company running 60+ parallel projects wants a PPM tool to prioritize initiatives, allocate resources, track benefits realization, and align projects with the company's 3-year digital health strategy.",
    businessProblem:
      "The company has 65 active projects with a combined budget of $28M but no centralized view of resource allocation, benefit delivery, or strategic alignment. 40% of projects are over budget or behind schedule. The PMO uses 7 different status reports from different teams, none in a consistent format. The board has lost confidence in the portfolio's ability to deliver the digital health strategy.",
    objectives: [
      "Create a single portfolio view of all 65 projects with status, budget, and resource data",
      "Reduce portfolio projects that are over budget or behind schedule from 40% to <15% within 18 months",
      "Enable evidence-based project prioritization aligned to the 3-year strategy",
      "Provide the PMO with a single source of truth for portfolio reporting",
      "Achieve 90% resource allocation accuracy across the portfolio",
    ],
    stakeholders: [
      { name: "CEO / Board", role: "Executive Sponsor", interest: "Strategic alignment, portfolio ROI, digital health strategy delivery", influence: "high" },
      { name: "CIO", role: "Business Owner", interest: "Portfolio health, resource optimization, delivery predictability", influence: "high" },
      { name: "PMO Director", role: "Business Owner", interest: "Standardized reporting, governance process support, less admin", influence: "high" },
      { name: "Project Managers (25)", role: "End Users", interest: "Minimal admin overhead, clear templates, useful tools", influence: "medium" },
      { name: "Resource Managers (8)", role: "End Users", interest: "Accurate demand forecasting, conflict resolution, capacity planning", influence: "medium" },
      { name: "Finance Director", role: "Stakeholder", interest: "Budget tracking, benefit realization, investment decisions", influence: "high" },
    ],
    requirements: [
      {
        title: "Portfolio dashboard with RAG status and financial tracking",
        raw_text: "The PMO must have a portfolio dashboard showing each project's: RAG status (Red/Amber/Green) for schedule, cost, and scope; budget (approved, actual, forecast); milestones (last completed, next due); strategic pillar alignment; and assigned project manager. Dashboard must be updated weekly and exportable to PowerPoint for board reporting.",
        req_type: "functional",
        feature: "portfolio dashboard",
        channel: "portal",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "portfolio management",
        assumptions: "Project managers will self-report status weekly via a structured 5-minute update form",
        constraints_list: "Board report export must be PowerPoint-compatible (not just PDF)",
      },
      {
        title: "Strategic alignment scoring and prioritization model",
        raw_text: "Each project must be scored against 5 strategic criteria (digital health enablement, patient outcomes, operational efficiency, regulatory compliance, revenue growth) with weightings defined by the executive team. Prioritization score must drive portfolio gating decisions. Projects below threshold score must be flagged for review.",
        req_type: "functional",
        feature: "prioritization",
        channel: null,
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "governance",
        assumptions: "Strategic weights approved by CIO and CEO before system configuration",
        constraints_list: "Scoring model must be auditable — no black-box algorithm; show component scores",
      },
      {
        title: "Resource capacity planning with demand vs supply view",
        raw_text: "Resource managers must see current and forecast demand by role and team for the next 12 months. The system must highlight over-allocation (>100% capacity) and under-utilization (<70% capacity) and suggest project rescheduling to resolve conflicts. Time entry must be project-coded.",
        req_type: "functional",
        feature: "resource management",
        channel: "portal",
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "resource management",
        assumptions: "Current capacity managed in Excel — will need migration",
        constraints_list: "Must support both named-resource and role-based resource planning",
      },
      {
        title: "Benefit realization tracking with milestone-linked KPIs",
        raw_text: "Each project must define 2–5 measurable benefits at approval. Benefits must be linked to project milestones. The system must track benefit realization post-go-live at 3, 6, and 12 months against the approved baseline. Benefits not on track must trigger a review gate automatically.",
        req_type: "functional",
        feature: "benefits realization",
        channel: null,
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "governance",
        assumptions: "Benefit baseline approved at project initiation by Finance Director",
        constraints_list: "Benefit KPIs must be SMART (specific, measurable, achievable, relevant, time-bound)",
      },
      {
        title: "Stage gate governance workflow",
        raw_text: "All projects above $250k must pass through defined stage gates (Idea, Discovery, Initiation, Delivery, Close). Each gate requires mandatory documentation (business case, risk register, resource plan) and sign-off from defined approvers. Projects missing gate criteria must be blocked from proceeding.",
        req_type: "functional",
        feature: "stage gate",
        channel: null,
        contains_phi: false,
        market: null,
        sensitivity_flags: [],
        priority: "must",
        domain: "governance",
        assumptions: "Stage gate criteria and approvers defined in PMO methodology document",
        constraints_list: "Gate approval must be electronic with digital signature; no paper approvals",
      },
    ],
    risks: [
      { id: "R1", description: "Project managers game the RAG status reporting to avoid scrutiny, making portfolio data unreliable", category: "Governance", likelihood: "high", impact: "high", mitigation: "Implement objective RAG criteria based on schedule variance and cost variance thresholds, not PM discretion; PMO reviews all Amber and Red statuses.", owner: "PMO Director" },
      { id: "R2", description: "Data migration from 7 different status reports is incomplete, creating an inaccurate baseline portfolio", category: "Data Quality", likelihood: "high", impact: "high", mitigation: "Run 4-week portfolio cleansing exercise before going live; each PM validates their project data; PMO Director signs off on baseline.", owner: "PMO Director" },
      { id: "R3", description: "Resource allocation data is inaccurate because PMs estimate rather than track actual time", category: "Data Quality", likelihood: "high", impact: "medium", mitigation: "Mandate weekly timesheet entry for all project staff; tie timesheet completion to performance review for PMs.", owner: "CIO" },
      { id: "R4", description: "Strategic scoring model is seen as political by PMs, leading to distrust and workarounds", category: "Change Management", likelihood: "medium", impact: "high", mitigation: "Co-design scoring criteria with PMs and resource managers; publish scoring rationale; run a scoring calibration workshop with the portfolio before launch.", owner: "PMO Director" },
      { id: "R5", description: "Stage gate process slows delivery rather than adding value, creating PMO vs delivery team conflict", category: "Operational", likelihood: "medium", impact: "high", mitigation: "Streamline gate documentation to the minimum viable set; set 5-day SLA for gate reviews; automate data population from project records.", owner: "PMO Director" },
    ],
    successMetrics: [
      "Single portfolio dashboard live within 6 months with ≥90% of projects reporting",
      "Projects over budget or behind schedule reduced from 40% to <15% within 18 months",
      "Board reporting time for PMO reduced from 3 days to ≤4 hours per month",
      "Resource over-allocation incidents reduced by ≥50% within 12 months",
      "100% of >$250k projects passing through stage gates within 9 months",
    ],
    practiceExercises: [
      "Design a strategic scoring model for the 3-year digital health strategy with 5 criteria and weightings",
      "Write a stage gate checklist for the 'Discovery' gate covering BA deliverables",
      "Create a resource demand forecast template for a 6-month period across 5 resource types",
      "Identify the top 3 governance failure modes in the current portfolio and write mitigations",
      "Draft a benefits realization plan for one of the case study projects in this library",
    ],
    tags: ["PPM", "Portfolio Management", "PMO", "Governance", "Resource Management", "Benefits Realization"],
  },
];

export const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

export const DIFFICULTY_COLORS: Record<Difficulty, string> = {
  beginner: "bg-green-50 text-green-700 border-green-200",
  intermediate: "bg-amber-50 text-amber-700 border-amber-200",
  advanced: "bg-red-50 text-red-700 border-red-200",
};
